import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Container, Tabs, Tab, Button } from "react-bootstrap";
import { ApiGet, ApiPut, ApiPost } from "../../../service/ApiService";
import SmartBillForm from "./SmartBill-ImportForm";
import RequestProducts from "./RequestProducts";
import { MdCached, MdLooks3, MdLooksOne, MdLooksTwo, MdSave } from "react-icons/md";

const RequestEdit = () => {
    const { id } = useParams(); // invoice request id from URL
    const [invoice, setInvoice] = useState(null);
    const [products, setProducts] = useState([]);
    const [mainError, setMainError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [invoiceSent, setInvoiceSent] = useState(false);
    const [invoiceSaved, setInvoiceSaved] = useState(false);

    // Ref to skip auto-update on initial load
    const hasInvoiceLoaded = useRef(false);

    useEffect(() => {
        // Fetch invoice request details and linked products
        ApiGet(
            `/api/smartbill/invoice/${id}`,
            (response) => {
                setInvoice(response);
                // Assume response contains a property "products" that is an array
                setProducts(response.products || []);
                setMainError(null);
            },
            (error) => setMainError("Eroare la preluarea solicitării.")
        );
    }, [id]);

    // Update invoice fields on change and mark as unsaved.
    const handleMainInputChange = (e) => {
        const { name, value } = e.target;
        setInvoiceSaved(false);
        setInvoice((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Separately define the update call (so it can be both manually invoked and via auto-update)
    const updateInvoice = () => {
        ApiPut(
            `/api/smartbill/invoice/${id}`,
            invoice,
            (response) => {
                setMainError(null);
                setInvoiceSaved(true);
                setInvoiceSent(false);
            },
            (error) => {
                setMainError("Eroare la actualizarea solicitării.");
                setInvoiceSaved(false);
            }
        );
    };

    // Manual submit from the form.
    const handleUpdateInvoice = (e) => {
        e.preventDefault();
        updateInvoice();
    };

    // Debounce auto-update when invoice changes (skip initial load)
    useEffect(() => {
        if (!invoice) return;
        if (!hasInvoiceLoaded.current) {
            hasInvoiceLoaded.current = true;
            return;
        }
        const timer = setTimeout(() => {
            // Auto-submit changes after 1 second of inactivity.
            updateInvoice();
        }, 1000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice]);

    const handleSendInvoice = () => {
        ApiPost(
            `/api/smartbill/invoice/send/${id}`,
            {},
            (response) => {
                const importResult = response.importResult;
                if (importResult && importResult.errorText) {
                    setMainError(`SmartBill a refuzat factura: ${importResult.errorText.replace(/<br\s*\/?>/gi, ' ')}`);
                    setSuccessMessage(null);
                } else if (importResult) {
                    const { message, number, series } = importResult;
                    setSuccessMessage(
                        `Succes: ${message}${number ? " - Număr: " + number : ""}${series ? " - Serie: " + series : ""
                        }`
                    );
                    setMainError(null);
                    setInvoiceSent(true); // Hide the form after a success send.
                } else {
                    setSuccessMessage("Factura a fost trimisă cu succes!");
                    setMainError(null);
                    setInvoiceSent(true);
                }
            },
            (error) => {
                setMainError("Eroare la trimiterea facturii." + (error.response?.data?.message || ""));
                setSuccessMessage(null);
            }
        );
    };

    if (!invoice) {
        return (
            <Container>
                <Alert variant="info">Se încarcă solicitarea...</Alert>
            </Container>
        );
    }

    // Hide the complete form only if the invoice has been sent successfully.
    if (invoiceSent) {
        return (
            <Container className="mt-5">
                <Alert variant="success">{successMessage}</Alert>
            </Container>
        );
    }

    return (
        <Container className="mt-5">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h2 className="mb-5">
                    Import factură in SmartBill <small style={{ color: 'grey' }}>#{id}</small>
                </h2>
                <span>
                    {invoiceSaved ? (
                        <MdSave color="green" title="Salvat" style={{ marginLeft: "10px", fontSize: "2rem" }} />
                    ) : (
                        <MdCached color="grey" title="Modificări nesalvate" style={{ marginLeft: "10px", fontSize: "2rem" }} />
                    )}
                </span>
            </div>
            {mainError && <Alert variant="danger">{mainError}</Alert>}
            {successMessage && <Alert variant="success">{successMessage}</Alert>}

            <Tabs defaultActiveKey="invoice" id="request-edit-tabs" className="mb-4">
                <Tab
                    eventKey="invoice"
                    title={
                        <span style={{ fontSize: "1.2rem", fontWeight: "bold", color: "green" }}>
                            <MdLooksOne /> Date factură
                        </span>
                    }
                >
                    <SmartBillForm
                        formValues={invoice}
                        handleInputChange={handleMainInputChange}
                        handleSubmit={handleUpdateInvoice}
                    />
                </Tab>
                <Tab
                    eventKey="products"
                    title={
                        <span style={{ fontSize: "1.2rem", fontWeight: "bold", color: "green" }}>
                            <MdLooksTwo /> Produse
                        </span>
                    }
                >
                    <RequestProducts invoiceId={id} products={products} setProducts={setProducts} />
                </Tab>
            </Tabs>

            <div className="d-grid gap-2 mt-5">
                <Button variant="success" size="lg" onClick={handleSendInvoice}>
                    <MdLooks3 /> Trimite Factura
                </Button>
            </div>
        </Container>
    );
};

export default RequestEdit;
